import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHero } from "@/components/Sections";
import { LeadForm } from "@/components/LeadForm";
import { Reveal } from "@/components/Reveal";
import { Button } from "@/components/ui/button";
import { Search, BellRing, Loader2, CheckCircle2, LogIn } from "lucide-react";
import { toast } from "sonner";

const HERO = "https://images.unsplash.com/photo-1601929862217-f1bf94503333?w=1600";

const num = (s) => {
  const n = parseInt(String(s || "").replace(/[^0-9]/g, ""), 10);
  return Number.isFinite(n) && n > 0 ? n : null;
};
const clean = (v) => (v && v !== "Peu importe" ? v : null);

function AlertCTA({ criteria }) {
  const { user } = useAuth();
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(false);

  if (!criteria) return null;

  const payload = {
    brand: criteria.brand || null,
    max_price: num(criteria.budget),
    fuel: clean(criteria.fuel),
    transmission: clean(criteria.transmission),
    min_year: num(criteria.year),
    max_mileage: num(criteria.mileage),
  };

  const create = async () => {
    setCreating(true);
    try {
      await api.post("/me/alerts", payload);
      setCreated(true);
      toast.success("Alerte créée — vous serez prévenu dès qu'un véhicule correspond");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setCreating(false); }
  };

  const wrap = (inner) => (
    <div data-testid="search-alert-cta" className="mt-6 border-t border-white/10 pt-6 text-center">
      <p className="flex items-center justify-center gap-2 text-sm text-neutral-300 mb-3">
        <BellRing className="h-4 w-4 text-[#C5A059]" /> Recevez une alerte dès qu'un véhicule correspond à cette recherche
      </p>
      {inner}
    </div>
  );

  if (created) {
    return wrap(<p className="flex items-center justify-center gap-2 text-[#C5A059] text-sm font-medium"><CheckCircle2 className="h-4 w-4" /> Alerte activée</p>);
  }
  if (!user) {
    return wrap(<Link to="/connexion"><Button data-testid="alert-cta-login" variant="outline" className="border-[#C5A059]/50 text-[#C5A059] hover:bg-[#C5A059]/10 rounded-sm"><LogIn className="h-4 w-4 mr-2" /> Connectez-vous pour créer une alerte</Button></Link>);
  }
  if (user.email_verified === false) {
    return wrap(<Link to="/compte"><Button variant="outline" className="border-white/20 text-white hover:bg-white/5 rounded-sm">Vérifiez votre e-mail pour activer les alertes</Button></Link>);
  }
  return wrap(
    <Button data-testid="alert-cta-create" onClick={create} disabled={creating} className="bg-[#C5A059] text-black hover:bg-[#E6C678] rounded-sm">
      {creating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}<BellRing className="h-4 w-4 mr-2" /> Créer une alerte avec ces critères
    </Button>
  );
}

export default function CustomSearch() {
  const [criteria, setCriteria] = useState(null);

  return (
    <div data-testid="custom-search-page">
      <PageHero
        overline="Recherche personnalisée"
        title="Votre voiture idéale existe peut-être déjà. Laissez-nous la trouver."
        subtitle="Indiquez-nous simplement vos critères : notre équipe recherche pour vous les meilleures opportunités correspondant à votre demande."
        image={HERO}
      />
      <section className="max-w-3xl mx-auto px-5 lg:px-8 py-24">
        <Reveal>
          <div className="bg-[#0f0f0f] border border-white/10 rounded-sm p-8 lg:p-10">
            <div className="flex items-center gap-3 mb-6">
              <Search className="h-6 w-6 text-[#C5A059]" />
              <h2 className="font-display text-2xl font-bold text-white">Déposez votre demande</h2>
            </div>
            <LeadForm
              type="recherche"
              submitLabel="Lancer la recherche"
              onSuccess={(form) => setCriteria(form)}
              successExtra={<AlertCTA criteria={criteria} />}
              fields={[
                { name: "brand", label: "Marque", type: "text", placeholder: "Ex : Volkswagen" },
                { name: "model", label: "Modèle", type: "text", placeholder: "Ex : Golf" },
                { name: "budget", label: "Budget", type: "text", placeholder: "Ex : 25 000 €" },
                { name: "year", label: "Année (à partir de)", type: "number", placeholder: "2018" },
                { name: "mileage", label: "Kilométrage max", type: "number", placeholder: "100000" },
                { name: "fuel", label: "Motorisation", type: "select", options: ["Essence", "Diesel", "Hybride", "Électrique", "Peu importe"] },
                { name: "transmission", label: "Boîte de vitesses", type: "select", options: ["Automatique", "Manuelle", "Peu importe"] },
                { name: "color", label: "Couleur souhaitée", type: "text", placeholder: "Ex : Gris, Noir..." },
                { name: "doors", label: "Nombre de portes", type: "select", options: ["3 portes", "5 portes", "Peu importe"] },
                { name: "equipment", label: "Équipements souhaités", type: "text", placeholder: "GPS, toit ouvrant..." },
                { name: "message", label: "Critères particuliers", type: "textarea", full: true },
              ]}
            />
          </div>
        </Reveal>
      </section>
    </div>
  );
}
